import { z } from 'zod/v4'

import { changeReasonSchema } from './change-reason.js'
import type { ContentJson } from './content-canonical.js'
import { CONTENT_VERSION_ORIGINS, CONTENT_VERSION_STATES } from './content-states.js'

/** Schemas of the content management API (#4060). Every object is strict: an undeclared key is refused, not stripped. */

export const contentTypeSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(64)

export const contentElementParamsSchema = z.strictObject({
  type: contentTypeSchema,
  key: z.string().min(1).max(300),
})
export type ContentElementParams = z.infer<typeof contentElementParamsSchema>

export const contentPayloadSchema: z.ZodType<Record<string, ContentJson>> = z.record(
  z.string(),
  z.json(),
)
export type ContentPayload = Record<string, ContentJson>

export const saveContentDraftBodySchema = z.strictObject({
  baseVersionId: z.guid().nullable(),
  payload: contentPayloadSchema,
  reason: changeReasonSchema,
})
export type SaveContentDraftBody = z.infer<typeof saveContentDraftBodySchema>

export const publishContentVersionBodySchema = z.strictObject({
  expectedBaseVersionId: z.guid().nullable(),
})
export type PublishContentVersionBody = z.infer<typeof publishContentVersionBodySchema>

export const restoreContentVersionBodySchema = z.strictObject({ reason: changeReasonSchema })
export type RestoreContentVersionBody = z.infer<typeof restoreContentVersionBodySchema>

export const retireContentElementBodySchema = z.strictObject({ reason: changeReasonSchema })
export type RetireContentElementBody = z.infer<typeof retireContentElementBodySchema>

export const contentVersionSchema = z.strictObject({
  id: z.guid(),
  versionNo: z.number().int().positive(),
  state: z.enum(CONTENT_VERSION_STATES),
  origin: z.enum(CONTENT_VERSION_ORIGINS),
  reason: z.string(),
  authorEmail: z.string().nullable(),
  baseVersionId: z.guid().nullable(),
  restoredFromVersionId: z.guid().nullable(),
  payloadSha256: z.string().regex(/^[0-9a-f]{64}$/),
  contractVersion: z.string().min(1),
  validFrom: z.iso.date().nullable(),
  validTo: z.iso.date().nullable(),
  createdAt: z.iso.datetime(),
  publishedAt: z.iso.datetime().nullable(),
})
export type ContentVersion = z.infer<typeof contentVersionSchema>

export const contentElementSchema = z.strictObject({
  type: contentTypeSchema,
  key: z.string().min(1).max(300),
  published: contentVersionSchema.nullable(),
  publicationSeq: z.number().int().nonnegative().nullable(),
  draft: contentVersionSchema.nullable(),
})
export type ContentElement = z.infer<typeof contentElementSchema>

export const saveContentDraftResponseSchema = z.strictObject({
  version: contentVersionSchema,
  unchanged: z.boolean(),
})
export type SaveContentDraftResponse = z.infer<typeof saveContentDraftResponseSchema>

export const publishContentResponseSchema = z.strictObject({
  versionNo: z.number().int().positive(),
  seq: z.number().int().nonnegative(),
})
export type PublishContentResponse = z.infer<typeof publishContentResponseSchema>
